import { useTranslation } from "react-i18next";
import { Invoice, PaymentStatus } from "@/api/opb/opbTypes";
import { formatCurrency } from "@/utils/string";
import { LoadingCircle } from "@industriousoffice/react-components";
import { ReactNode } from "react";

interface InvoiceSummaryProps {
  invoices: Invoice[];
  creditCardFeeToPay?: string;
  totalPaymentToPay: string;
  currencyCode?: string;
  isLoading?: boolean;
  footer?: ReactNode;
  className?: string;
}

export function InvoiceSummary({
  invoices,
  creditCardFeeToPay,
  totalPaymentToPay,
  currencyCode,
  isLoading,
  footer,
  className = "",
}: InvoiceSummaryProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation("payInvoices");

  return (
    <div className={`pt-24 pr-5 h-full ${className}`}>
      <span
        className="text-2xl font-bold"
        data-testid="pay_invoices_summary-title"
      >
        {t("Payment Summary")}
      </span>
      {isLoading ? (
        <div className="flex items-center justify-center py-5">
          <LoadingCircle spinnerColor="#013e3f" />
        </div>
      ) : (
        <>
          {currencyCode ? (
            <div className="mt-6 mb-4 text-sm">
              {invoices.map((invoice) => (
                <div className="flex justify-between mb-2" key={invoice.id}>
                  <div className="text-grey-30">{invoice.invoiceNumber}</div>
                  <div
                    className="font-semibold"
                    data-testid={`pay_invoices_summary-invoice-amount-due-${invoice.id}`}
                  >
                    {formatCurrency({
                      value:
                        invoice.paymentStatus !== PaymentStatus.Paid
                          ? invoice.balance
                          : invoice.amount,
                      currency: currencyCode,
                      lang: language,
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              ))}
              {creditCardFeeToPay && (
                <div className="flex justify-between mb-2">
                  <div className="text-grey-30">
                    {t("Card Processing Fee")}*
                  </div>
                  <div
                    className="font-semibold"
                    data-testid="pay_invoices_summary-credit-card-fee-due"
                  >
                    {creditCardFeeToPay}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          <div className="flex items-center justify-between pt-4 border-t border-grey-60">
            <div className="font-bold ">{t("Total")}</div>
            <div
              className="text-xl font-bold"
              data-testid="pay_invoices_summary-total-due"
            >
              {totalPaymentToPay}
            </div>
          </div>
          {footer}
        </>
      )}
    </div>
  );
}
